import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { DataPanel } from "react-migration/components/DataPanel";
import { useVariants } from "react-migration/components/TextWithVariants";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { KeyIndicatorColor, useKeyIndicatorTags } from "react-migration/lib/map/useKeyIndicatorTags";
import { getGeometryMeasurements } from "react-migration/lib/util/getAreaAndPerimeter";
import { userStore } from "src/js/stores/user/store";
import { LayerTypeLayerSummaryProps, SupportedRegions } from "../../types";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { usePropertyInformationByUPRNQuery } from "react-migration/domains/ownership/apollo/usePropertyInformationByUPRNQuery";

export function OwnershipPlotSizeLayerSummary({ selection, ...rest }: LayerTypeLayerSummaryProps) {
  return hasFeature(Feature.usAccess) ? (
    <OwnershipPlotSizeLayerSummaryUS selection={selection} {...rest} />
  ) : (
    <OwnershipPlotSizeLayerSummaryUK selection={selection} {...rest} />
  );
}

function OwnershipPlotSizeLayerSummaryUK({ selection }: LayerTypeLayerSummaryProps) {
  const { user } = useSnapshot(userStore);
  const { t } = useTranslation();

  const geometry = selection.feature?.geometry ?? null;

  const measurements = useMemo(() => {
    return geometry && getGeometryMeasurements(geometry, user.settings.unit);
  }, [geometry, user.settings.unit]);

  const [areaVariant, toggleAreaVariant] = useVariants(measurements?.areaVariants, measurements?.areaIsLarge);
  const [perimeterVariant, togglePerimeterVariant] = useVariants(
    measurements?.perimeterVariants,
    measurements?.perimeterIsLarge
  );

  useKeyIndicatorTags(() => {
    return [
      {
        tag: areaVariant,
        color: KeyIndicatorColor.DEFAULT,
      },
    ];
  }, [areaVariant]);

  return (
    <DataPanel.Grid>
      <DataPanel.Cell title={t("components.measure2D.area")}>
        <DataPanel.Button onClick={toggleAreaVariant}>{areaVariant}</DataPanel.Button>
      </DataPanel.Cell>
      <DataPanel.Cell title={t("components.measure2D.perimeter")}>
        <DataPanel.Button onClick={togglePerimeterVariant}>{perimeterVariant}</DataPanel.Button>
      </DataPanel.Cell>
    </DataPanel.Grid>
  );
}

function OwnershipPlotSizeLayerSummaryUS({ selection }: LayerTypeLayerSummaryProps) {
  const { user } = useSnapshot(userStore);
  const { t } = useTranslation();
  const { propertyInformation } = usePropertyInformationByUPRNQuery(selection.id);
  const geometry = selection.feature?.geometry ?? null;

  const measurements = useMemo(() => {
    return geometry && getGeometryMeasurements(geometry, user.settings.unit);
  }, [geometry, user.settings.unit]);

  const [areaVariant, toggleAreaVariant] = useVariants(measurements?.areaVariants, measurements?.areaIsLarge);

  useKeyIndicatorTags(() => {
    return [
      {
        tag: areaVariant,
        color: KeyIndicatorColor.DEFAULT,
      },
    ];
  }, [areaVariant]);

  return (
    <DataPanel.Grid columns={2}>
      <DataPanel.Cell title={t("components.measure2D.area")}>
        <DataPanel.Button onClick={toggleAreaVariant}>{areaVariant}</DataPanel.Button>
      </DataPanel.Cell>
      <DataPanel.Cell title={t("property_information.sidebar.vacant_lot_title")}>
        {propertyInformation?.vacant_lot || t("not_applicable.long")}
      </DataPanel.Cell>
    </DataPanel.Grid>
  );
}

OwnershipPlotSizeLayerSummary.icon = "icon-lt-collage-line";
OwnershipPlotSizeLayerSummary.hideLink = true;
OwnershipPlotSizeLayerSummary.supportedRegions = [
  SupportedRegions.england,
  SupportedRegions.scotland,
  SupportedRegions.wales,
  SupportedRegions.us,
];
